// Staff-friendly wizard rendered in place of the generic workflow panel when
// the active workflow is `instructor_onboarding`. Uses plain language, a 6-step
// progress track, and after the instructor is added, guides the admin to the
// next logical step (assign a class, set pay, send documents, etc.) by sending
// prefilled prompts to the assistant.
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { friendlyError } from "@/lib/friendlyError";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  UserPlus,
  Save,
  X,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
} from "lucide-react";
import type { WorkflowView } from "./CurrentWorkflowPanel";

type Busy = null | "add" | "save" | "cancel" | "review";

interface Props {
  workflow: WorkflowView;
  functionName: string;
  onChange: (workflow: WorkflowView | null) => void;
  onSendMessage?: (text: string) => void;
  forceOpenNonce?: number;
}

const STEPS = [
  { key: "profile", label: "Profile" },
  { key: "class", label: "Class" },
  { key: "pay", label: "Pay" },
  { key: "documents", label: "Documents" },
  { key: "packet", label: "Packet" },
  { key: "review", label: "Review" },
] as const;

export function InstructorOnboardingWizard({
  workflow,
  functionName,
  onChange,
  onSendMessage,
  forceOpenNonce = 0,
}: Props) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<Busy>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (forceOpenNonce > 0) setOpen(true);
  }, [forceOpenNonce]);

  const collected = workflow.collected ?? {};
  const ctx = workflow.context ?? {};
  const instructorId: string | undefined = ctx.instructor_id;
  const displayName: string =
    (collected.preferred_name as string) ||
    (collected.full_name as string) ||
    "this instructor";

  const errorCount = workflow.validation.filter((v) => v.level === "error").length;
  const missingRequired = workflow.missing.length > 0;
  const validationBlocked = errorCount > 0;
  const completed = workflow.status === "completed";
  const readyToAdd =
    !completed && !missingRequired && !validationBlocked && !instructorId;
  const needsReviewGate = workflow.status === "draft" && !missingRequired && !validationBlocked;

  const currentStep = useMemo<number>(() => {
    if (completed) return 6;
    if (!instructorId) return 1;
    if (collected.class_slug) {
      // has class → check next incomplete step
      if (!(collected.pay_rate_amount || collected.hourly_rate || collected.one_time_payment_amount)) return 3;
      if (!collected.w9_status || collected.w9_status === "Not Requested") return 4;
      if (!collected.send_onboarding_packet) return 5;
      return 6;
    }
    return 2;
  }, [completed, instructorId, collected]);

  const call = async (action: string, extra: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: { action, draft_id: workflow.id, ...extra },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  // Add Instructor: if the workflow needs a review gate first, run it silently
  // so the admin only sees one click.
  const doAddInstructor = async () => {
    setBusy("add");
    try {
      if (needsReviewGate) {
        const r = await call("workflow_prepare_review");
        onChange(r?.workflow ?? null);
      }
      const r = await call("workflow_execute");
      onChange(r?.view ?? null);
      const results = r?.results ?? [];
      const failed = results.filter((x: any) => !x.ok);
      if (failed.length === 0) {
        toast.success(`${displayName} has been added to the Instructor Directory.`);
      } else {
        friendlyError({ what: `I couldn't finish adding ${displayName}.`, meaning: "Some steps didn't complete.", changed: "The completed steps have been saved.", nextStep: "Review the flagged step and try again.", technical: failed[0].error });
      }
    } catch (e: unknown) {
      friendlyError({ what: `I couldn't add ${displayName} yet.`, technical: e });
    } finally {
      setBusy(null);
    }
  };

  const doSave = async () => {
    setBusy("save");
    try {
      const r = await call("workflow_save");
      onChange(r?.workflow ?? null);
      toast.success("Saved. Pick up where you left off any time.");
    } catch (e: unknown) {
      friendlyError({ what: "I couldn't save this setup as a draft.", technical: e });
    } finally {
      setBusy(null);
    }
  };

  const doCancel = async () => {
    if (!confirm(`Discard this setup for ${displayName}? Nothing will be saved.`)) return;
    setBusy("cancel");
    try {
      await call("workflow_cancel");
      onChange(null);
    } catch (e: unknown) {
      friendlyError({ what: `I couldn't discard this setup for ${displayName}.`, technical: e });
    } finally {
      setBusy(null);
    }
  };

  const openProfile = () => {
    if (instructorId) {
      navigate(`/admin/instructors?focus=${instructorId}`);
    } else {
      navigate(`/admin/instructors`);
    }
  };

  const sendPrompt = (text: string) => {
    if (onSendMessage) onSendMessage(text);
    else toast.info("Type your next instruction in the chat below.");
  };

  // Direct workflow start with the instructor context carried over. This is
  // used by the "Assign to an existing class" button so the admin is never
  // asked to identify the instructor again — MAVIS knows exactly who we're
  // working with.
  const startAssignmentWorkflow = async () => {
    if (!instructorId) {
      toast.info(`Add ${displayName} first, then I'll help you assign a class.`);
      return;
    }
    setBusy("add");
    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: {
          action: "workflow_start",
          workflow_type: "instructor_assignment",
          title: `Assign ${displayName} to a class`,
          seed: {
            instructor_name: displayName,
            instructor_query: displayName,
          },
          context: {
            instructor_id: instructorId,
            instructor_name: displayName,
            origin_workflow: "instructor_onboarding",
          },
        },
      });
      if (error) throw new Error(error.message);
      onChange(data?.workflow ?? null);
    } catch (e: unknown) {
      friendlyError({ what: `I couldn't start the class assignment for ${displayName}.`, technical: e });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="border-primary/30 bg-primary/5">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-primary/20">
        <Badge variant="outline" className="text-[10px] uppercase">
          Instructor Setup
        </Badge>
        <div className="font-medium text-sm truncate flex-1">
          {completed ? `${displayName} — Setup progress` : `Setting up ${displayName}`}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {/* Step tracker */}
      <div className="px-3 pt-3">
        <StepTrack current={currentStep} />
        <Progress value={(currentStep / STEPS.length) * 100} className="h-1.5 mt-2" />
      </div>

      {open && (
        <div className="px-3 py-3 space-y-3 text-sm">
          {/* Collected summary */}
          {Object.keys(collected).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                What we have so far
              </div>
              <ul className="space-y-0.5">
                {Object.entries(collected)
                  .filter(([, v]) => v !== null && v !== undefined && v !== "")
                  .slice(0, 8)
                  .map(([k, v]) => (
                    <li key={k} className="text-xs flex gap-2">
                      <CheckCircle2 className="h-3 w-3 text-primary shrink-0 mt-0.5" />
                      <span className="text-muted-foreground min-w-[110px]">{prettyKey(k)}:</span>
                      <span className="font-medium truncate">{formatVal(v)}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/* Still needed */}
          {workflow.missing.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                Still needed to add {displayName}
              </div>
              <ul className="space-y-0.5">
                {workflow.missing.map((m) => (
                  <li key={m.key} className="text-xs text-amber-700 dark:text-amber-400 flex gap-2">
                    <span>•</span> {m.label}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-1">
                Type the missing details into the chat below — we'll add them to this setup.
              </p>
            </div>
          )}

          {/* Validation errors */}
          {workflow.validation.length > 0 && (
            <div className="space-y-1">
              {workflow.validation.map((v, i) => (
                <div
                  key={i}
                  className={`text-xs flex gap-1.5 ${
                    v.level === "error" ? "text-destructive" : "text-amber-600"
                  }`}
                >
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {v.message}
                </div>
              ))}
            </div>
          )}

          {/* PRIMARY: Step 1 — Add Instructor */}
          {!instructorId && !completed && (
            <div className="rounded border border-primary/20 bg-background p-3 space-y-2">
              <div className="text-sm font-medium">Ready to add {displayName}?</div>
              <p className="text-xs text-muted-foreground">
                Clicking <strong>Add Instructor</strong> will create {displayName}'s profile and add
                them to the Instructor Directory. You can set their class, pay, and documents in
                the next steps.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" onClick={doAddInstructor} disabled={!readyToAdd || !!busy}>
                  {busy === "add" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <UserPlus className="h-3.5 w-3.5 mr-1" />
                  )}
                  Add Instructor
                </Button>
                <Button size="sm" variant="outline" onClick={doSave} disabled={!!busy}>
                  {busy === "save" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1" />
                  )}
                  Finish later
                </Button>
                <Button size="sm" variant="ghost" onClick={doCancel} disabled={!!busy}>
                  <X className="h-3.5 w-3.5 mr-1" /> Discard
                </Button>
              </div>
              {!readyToAdd && (missingRequired || validationBlocked) && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Add the missing details above before we can create {displayName}.
                </p>
              )}
            </div>
          )}

          {/* AFTER-ADD: Step 2+ — What's next? */}
          {(instructorId || completed) && (
            <div className="rounded border border-primary/20 bg-background p-3 space-y-2">
              <div className="text-sm font-medium">
                {displayName} has been added to the Instructor Directory. What would you like to
                do next?
              </div>
              <div className="grid gap-2 sm:grid-cols-2 pt-1">
                <NextStepButton
                  label="Assign to an existing class"
                  hint="Pick from current classes and set an effective date."
                  onClick={startAssignmentWorkflow}
                />
                <NextStepButton
                  label="Create a new class"
                  hint={`Start a new class with ${displayName} as the instructor.`}
                  onClick={() =>
                    sendPrompt(`Create a new class with ${displayName} as the instructor.`)
                  }
                />
                <NextStepButton
                  label="Add as a substitute"
                  hint="Temporary coverage for an existing class."
                  onClick={() =>
                    sendPrompt(`Add ${displayName} as a temporary substitute. Which class?`)
                  }
                />
                <NextStepButton
                  label="Set pay arrangement"
                  hint="Pay type, rate, and payment method."
                  onClick={() =>
                    sendPrompt(`Set the pay arrangement for ${displayName}.`)
                  }
                />
                <NextStepButton
                  label="Request or upload documents"
                  hint="W-9, teaching agreement, background check."
                  onClick={() =>
                    sendPrompt(`Send the required onboarding documents to ${displayName}.`)
                  }
                />
                <NextStepButton
                  label="Send the onboarding packet"
                  hint="Emails a secure onboarding link."
                  onClick={() =>
                    sendPrompt(`Send the onboarding packet to ${displayName}.`)
                  }
                />
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t mt-2">
                <Button size="sm" variant="outline" onClick={openProfile}>
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Open {displayName}'s Profile
                </Button>
                <Button size="sm" variant="ghost" onClick={doSave} disabled={!!busy || completed}>
                  {busy === "save" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1" />
                  )}
                  Save and finish later
                </Button>
                {!completed && (
                  <Button size="sm" variant="ghost" onClick={doCancel} disabled={!!busy}>
                    <X className="h-3.5 w-3.5 mr-1" /> Close setup
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function StepTrack({ current }: { current: number }) {
  return (
    <ol className="flex items-center gap-1 text-[11px]" aria-label="Onboarding steps">
      {STEPS.map((s, i) => {
        const step = i + 1;
        const state = step < current ? "done" : step === current ? "active" : "todo";
        return (
          <li key={s.key} className="flex items-center gap-1 min-w-0">
            <span
              className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-semibold shrink-0 ${
                state === "done"
                  ? "bg-primary text-primary-foreground"
                  : state === "active"
                  ? "bg-primary/20 text-primary border border-primary"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {state === "done" ? "✓" : step}
            </span>
            <span
              className={`truncate ${
                state === "active"
                  ? "font-medium text-foreground"
                  : "text-muted-foreground"
              }`}
            >
              {s.label}
            </span>
            {step < STEPS.length && (
              <ArrowRight className="h-3 w-3 text-muted-foreground/60 shrink-0" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function NextStepButton({
  label,
  hint,
  onClick,
}: {
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="text-left rounded border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors px-3 py-2"
    >
      <div className="text-xs font-medium">{label}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
    </button>
  );
}

function prettyKey(k: string) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function formatVal(v: any) {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null || v === undefined) return "—";
  return String(v);
}
